import React, { useEffect, useMemo, useState, useRef, useContext, useCallback } from "react";
import * as yup from "yup";
import {
    OrderCheckoutCreate,
    OrderCheckoutCreateFields,
    OrderCheckoutFields,
    OrderCheckoutFinish,
    OrderCheckoutFinishFields,
    OrderCheckoutUpdate,
    OrderDeliveryAddress,
    OrderDeliveryAddressFields,
    DOC_DNI_ID,
    OrderPersonalInformation,
    OrderPersonalInformationFields,
    OrderProduct,
    OrderProductFields,
    OrderShippingViewDTO,
    OrderShippingViewDTOFields,
    OrderCalculationRequest,
    OrderCalculationRequestFields,
    OrderCalculationView,
    OrderCalculationViewFields,
    OrderFinished,
    OrderFinishedFields,
    OrderFinishedInsufficientStockProduct,
} from "../../../types/purchaseOrder/Order";
import { useForm, UseFormReturn, FieldErrors } from "react-hook-form";
import { Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import {
    RequiredMailSchema,
    RequiredNumberSchema,
    RequiredSelectSchema,
    RequiredStringSchema,
    cardNumberMatchesSelectedBrand,
} from "@ecommerce/shared-utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { MapPinIcon, TruckIcon } from "@phosphor-icons/react";
import {
    HttpOrder,
    HttpShippings,
    HttpUserClient,
    HttpUserAddress,
    HttpAuth,
    HttpPaymentOption,
    HttpCache,
    HttpCamposAdicionales,
} from "../../../http";
import {
    SuscripcionCampoAdicionalVista,
    OrdenCampoAdicionalValorDTO,
    TIPO_CAMPO_ADICIONAL,
} from "../../../types/camposAdicionales";
import type { ShippingCalculationRequest } from "../../../http/httpShippings";
import { useCart } from "@ecommerce/store";
import { BaseResponseFields, EntityWithIdFields } from "../../../types/baseEntities";
import { CartItem } from "@ecommerce/store";
import {
    AppliedCouponDiscount,
    AppliedCouponDiscountFields,
} from "../../../types/purchaseOrder/couponTypes";
import { useSnackbar } from "notistack";
import { useShipping, CheckoutResumeContext } from "@ecommerce/checkout";
import {
    validateInternationalPhone,
    digitsOnly,
    formatInternationalPhoneForSummary,
} from "@ecommerce/shared-utils";
import { useUser } from "@ecommerce/store";

import type { UserViewDTO } from "../../../types/user/UserView";
import { UserClientFields } from "../../../types/user/userClient";
import { UserAddressFields } from "../../../types/user/userAddress";
import { AppConfigFields } from "../../../types/appConfigEntities";
import {
    OrderPaymentOptionViewDTO,
    OrderPaymentOptionViewDTOFields,
    OrderPaymentData,
    CardTypeViewDTO,
} from "../../../types/purchaseOrder/Order";

import { PROVEEDOR_PAGO } from "../checkoutConstants";

const MP_FAILURE_RESUME_GUID_KEY = "mp_failure_resume_guid";

const clearRootCartIfMpFailureResume = (orderGuid: string) => {
    const flagged = sessionStorage.getItem(MP_FAILURE_RESUME_GUID_KEY);
    if (flagged && flagged.toLowerCase() === orderGuid.toLowerCase()) {
        useCart.getState().clearCart();
        sessionStorage.removeItem(MP_FAILURE_RESUME_GUID_KEY);
    }
};

export enum CheckoutFormTypeFields {
    Subscribe = "subscribe",
    PersonalInformation = "personalInformation",
    DeliveryType = "deliveryType",
    DeliveryAddress = "deliveryAddress",
    SameDeliveryAddress = "sameDeliveryAddress",
    ShippingAddress = "shippingAddress",
    CouponStatus = "couponStatus",
    PaymentOptionId = "paymentOptionId",
}

export interface CheckoutFormType {
    [CheckoutFormTypeFields.Subscribe]: boolean;
    [CheckoutFormTypeFields.PersonalInformation]: OrderPersonalInformation;
    [CheckoutFormTypeFields.DeliveryType]?: string;
    [CheckoutFormTypeFields.DeliveryAddress]: OrderDeliveryAddress;
    [CheckoutFormTypeFields.SameDeliveryAddress]: boolean;
    [CheckoutFormTypeFields.ShippingAddress]?: OrderDeliveryAddress;
    [CheckoutFormTypeFields.CouponStatus]: CouponStatus;
    [CheckoutFormTypeFields.PaymentOptionId]?: string;
}

export enum CheckoutProcessStep {
    ZipCode,
    CustomerDetails,
    PaymentAndConfirmation,
    Finished,
}

export enum CheckoutProcessStatusType {
    InProgress,
    FinishedSuccessfully,
    FinishedWithError,
    RequiresStockResolution,
}

export interface CheckoutProcessStatusItem {
    name: string;
    image: string;
    quantity: number;
    price: number;
    attributes?: { name: string; value: string }[];
}

export interface CheckoutProcessStatus {
    type: CheckoutProcessStatusType;
    description?: string;
    insufficientStockProducts?: OrderFinishedInsufficientStockProduct[];
    orderNumber?: string;
    customerEmail?: string;
    customerName?: string;
    customerPhone?: string;
    deliveryAddress?: string | null;
    zipCode?: string;
    items?: CheckoutProcessStatusItem[];
}

export interface CouponStatus {
    loading: boolean;
    error: string | null;
    success: boolean;
    appliedDiscount: AppliedCouponDiscount | null;
    calculationData: OrderCalculationView | null;
}

const isHomeDeliveryByType = (
    deliveryType: string | undefined,
    shippingOptions: OrderShippingViewDTO[],
) => {
    if (!deliveryType) return false;
    const selectedOption = shippingOptions.find(
        (opt) => opt[EntityWithIdFields.Id] === deliveryType,
    );
    return selectedOption?.[OrderShippingViewDTOFields.RequestAddress] ?? false;
};

const resolveShippingCost = (option?: OrderShippingViewDTO | null) => {
    if (!option) {
        return {
            cost: null,
            isToBeAgreed: false,
        };
    }

    const isToBeAgreed =
        !option[OrderShippingViewDTOFields.FixedCost] &&
        !option[OrderShippingViewDTOFields.IntegratedCost];

    return {
        cost: isToBeAgreed ? 0 : option[OrderShippingViewDTOFields.Cost],
        isToBeAgreed,
    };
};

const makeDeliveryValue = (shippingOptionId: string, branchId: string | null) =>
    `${shippingOptionId}::${branchId ?? ""}`;

const parseDeliveryValue = (value?: string | null) => {
    if (!value) return { optionId: null as string | null, branchId: null as string | null };

    const parts = String(value).split("::");
    const optionId = parts[0] || null;
    const branchId = parts.length > 1 && parts[1] ? parts[1] : null;

    return { optionId, branchId };
};

// Branch helpers robustos (porque sucursales viene como any[])
const getBranchId = (b: any, idx: number) =>
    String(b?.id ?? b?.[EntityWithIdFields.Id] ?? b?.uniIdSucursal ?? b?.idSucursal ?? idx);

const getBranchName = (b: any) =>
    String(b?.nombre ?? b?.name ?? b?.descripcion ?? b?.desc ?? "Sucursal");

const getBranchAddress = (b: any) =>
    (b?.direccion ?? b?.address ?? b?.domicilio ?? b?.calle ?? null) as string | null;

export interface DeliveryOptionItem {
    value: string;
    title: string;
    description: string;
    price: string;
    priceNumber: number | null;
    isToBeAgreed: boolean;
    summary: string;
}

export interface DeliveryOptionGroup {
    Icon: React.ElementType;
    title: string;
    options: DeliveryOptionItem[];
}

interface CheckoutFormContextType {
    methods: UseFormReturn<CheckoutFormType>;
    currentStep: CheckoutProcessStep;
    setCurrentStep: React.Dispatch<React.SetStateAction<CheckoutProcessStep>>;
    processStatus: CheckoutProcessStatus;
    confirmedZipCode: boolean;
    onCancelZipCode: () => void;
    changeDeliveryZip: (zip: string) => Promise<void>;
    onSubmitZip: () => Promise<void>;
    onSubmitCustomerDetail: () => void;
    onSubmitOrder: () => void;
    deliveryType: string | undefined;
    setDeliveryType: (value: string) => void;
    isHomeDelivery: boolean;
    deliveryOptions: DeliveryOptionGroup[];
    zipCode: string | undefined;
    showCouponField: boolean;
    setShowCouponField: React.Dispatch<React.SetStateAction<boolean>>;
    couponCode: string;
    setCouponCode: React.Dispatch<React.SetStateAction<string>>;
    cartItems: CartItem[];
    couponStatus: CouponStatus;
    setCouponStatus: (status: CouponStatus) => void;
    applyCoupon: (nameCoupon: string) => void;
    isLoadingZipCode: boolean;
    isLoadingCustomerDetail: boolean;
    isLoadingOrder: boolean;
    recalculateOrderWithoutCoupon: () => Promise<void>;
    recalculateOrder: (productsOverride?: OrderProduct[]) => Promise<OrderCalculationView | null>;
    shippingOptions: OrderShippingViewDTO[];
    minPurchaseAmount: number;
    minPurchaseSubtotal: number;
    minPurchaseMissing: number;
    isCartEmpty: boolean;
    isCheckoutBlocked: boolean;
    paymentOptions: OrderPaymentOptionViewDTO[];
    isLoadingPaymentOptions: boolean;
    paymentOptionId: string | undefined;
    setPaymentOptionId: (value: string) => void;
    setPaymentData: (updater: React.SetStateAction<OrderPaymentData | null>) => void;
    getPaymentData: () => OrderPaymentData | null;
    selectedPaymentProviderCode: number | null;
    cardTypes: CardTypeViewDTO[];
    isLoadingCardTypes: boolean;
    lstCamposAdicionales: SuscripcionCampoAdicionalVista[];
    camposAdicionalesValores: Record<string, string>;
    setCampoAdicionalValor: (uniId: string, valor: string) => void;
}

export function CheckoutFormContext() {
    const resumeCtx = useContext(CheckoutResumeContext);

    let [searchParams] = useSearchParams();
    const orderGuidParam = searchParams.get("guid") || "";

    const isResumeFlow =
        !!resumeCtx?.resumeToken || !!resumeCtx?.orderGuid || !!(resumeCtx as any)?.resume;

    const shouldSkipProfileAutofill = isResumeFlow || !!orderGuidParam;

    /*const { cartItems: itemsFromCart, clearCart } = useCart();*/
    const { cartItems, clearCart } = useCart();

    const { enqueueSnackbar } = useSnackbar();
    const {
        zipCode: contextZipCode,
        selectedShippingOptionId: contextShippingOptionId,
        selectedShippingOptionBranchId: contextShippingOptionBranchId,
        setZipCode: setContextZipCode,
        setSelectedShippingOptionId: setContextShippingOptionId,
        setSelectedShippingOptionBranchId: setContextShippingOptionBranchId,
    } = useShipping();

    /* const [cartItems, _] = useState<CartItem[]>(itemsFromCart);*/
    const [currentStep, setCurrentStep] = useState<CheckoutProcessStep>(
        CheckoutProcessStep.ZipCode,
    );
    const [processStatus, setProcessStatus] = useState<CheckoutProcessStatus>({
        type: CheckoutProcessStatusType.InProgress,
    });

    const initialOrderGuid = resumeCtx?.orderGuid || orderGuidParam;
    const [orderGuid, setOrdenGuid] = useState<string>(initialOrderGuid);

    const orderGuidRef = useRef<string>(initialOrderGuid);

    const isRedirectingToMpRef = useRef(false);

    const setOrderGuidSafe = (guid: string) => {
        orderGuidRef.current = guid;
        setOrdenGuid(guid);
    };

    const [showCouponField, setShowCouponField] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [confirmedZipCode, setConfirmedZipCode] = useState<boolean>(false);

    const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);
    const [isLoadingCustomerDetail, setIsLoadingCustomerDetail] = useState(false);
    const [isLoadingOrder, setIsLoadingOrder] = useState(false);

    const [shippingOptions, setShippingOptions] = useState<OrderShippingViewDTO[]>([]);

    const [paymentOptions, setPaymentOptions] = useState<OrderPaymentOptionViewDTO[]>([]);
    const [isLoadingPaymentOptions, setIsLoadingPaymentOptions] = useState(false);
    const paymentDataRef = useRef<OrderPaymentData | null>(null);
    const setPaymentData = useCallback((updater: React.SetStateAction<OrderPaymentData | null>) => {
        paymentDataRef.current =
            typeof updater === "function" ? updater(paymentDataRef.current) : updater;
    }, []);
    const getPaymentData = useCallback(() => paymentDataRef.current, []);
    const [cardTypes, setCardTypes] = useState<CardTypeViewDTO[]>([]);
    const [isLoadingCardTypes, setIsLoadingCardTypes] = useState(false);

    const [lstCamposAdicionales, setLstCamposAdicionales] = useState<
        SuscripcionCampoAdicionalVista[]
    >([]);
    const [camposAdicionalesValores, setCamposAdicionalesValores] = useState<
        Record<string, string>
    >({});
    const camposAdicionalesValoresRef = useRef<Record<string, string>>({});

    const setCampoAdicionalValor = useCallback((uniId: string, valor: string) => {
        camposAdicionalesValoresRef.current = {
            ...camposAdicionalesValoresRef.current,
            [uniId]: valor,
        };
        setCamposAdicionalesValores({ ...camposAdicionalesValoresRef.current });
    }, []);

    useEffect(() => {
        setIsLoadingPaymentOptions(true);
        HttpPaymentOption.getPaymentOptions()
            .then((opts) => setPaymentOptions(opts ?? []))
            .catch(() => {
                enqueueSnackbar("No se pudieron cargar los métodos de pago.", { variant: "error" });
            })
            .finally(() => setIsLoadingPaymentOptions(false));
    }, []);

    useEffect(() => {
        setIsLoadingCardTypes(true);
        HttpCache.getTiposTarjeta()
            .then((tipos) => setCardTypes(tipos ?? []))
            .catch(() => {
                /* silencioso — fallback al array vacío */
            })
            .finally(() => setIsLoadingCardTypes(false));
    }, []);

    useEffect(() => {
        HttpCamposAdicionales.obtenerDeOrden()
            .then((campos) => setLstCamposAdicionales(campos ?? []))
            .catch(() => {
                /* silencioso */
            });
    }, []);

    const creatingOrderRef = useRef(false);
    const lastSyncedMailRef = useRef<string | null>(null);
    const lastSyncedZipRef = useRef<string | null>(null);
    const lastSyncedDeliveryTypeRef = useRef<string | null>(null);
    const lastSyncedShippingCostRef = useRef<number | null>(null);
    const lastSyncedDeliveryBranchRef = useRef<string | null>(null);
    const lastSyncedPaymentOptionRef = useRef<string | null>(null);

    const isEmptyLike = (v: any) => {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim().length === 0;
        if (typeof v === "number") return v === 0;
        return false;
    };

    const setIfUserHasNotEdited = (path: any, value: any) => {
        if (value === undefined) return;

        const state = methods.getFieldState(path);
        if (state?.isDirty || state?.isTouched) return;

        const current = methods.getValues(path);
        if (!isEmptyLike(current)) return;

        methods.setValue(path, value, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
        });
    };

    const onlyDigits = (s?: string | null) => (s ?? "").replace(/\D/g, "");

    const clients = useUser((s) => s.clients);
    const addresses = useUser((s) => s.addresses);
    const setClients = useUser((s) => s.setClients);
    const setAddresses = useUser((s) => s.setAddresses);

    const activeClient = useMemo(
        () => (clients ?? []).find((c: any) => !!c?.seleccionado) ?? null,
        [clients],
    );

    const activeAddress = useMemo(
        () => (addresses ?? []).find((a: any) => !!a?.seleccionado) ?? null,
        [addresses],
    );

    const [user, setUser] = useState<UserViewDTO | null>(null);

    // flags anti-loop
    const didLoadProfileDataRef = useRef(false);
    const shouldAutoSubmitZipRef = useRef(false);
    const didAutoSubmitZipRef = useRef(false);

    const checkoutFormTypeSchema = useMemo(
        () =>
            yup.object().shape({
                [CheckoutFormTypeFields.Subscribe]: yup.boolean().optional(),
                [CheckoutFormTypeFields.PersonalInformation]: yup.object().shape({
                    [OrderPersonalInformationFields.Email]: RequiredMailSchema,
                    [OrderPersonalInformationFields.DocumentoTipo]: yup
                        .number()
                        .nullable()
                        .optional(),
                    [OrderPersonalInformationFields.FirstName]: yup
                        .string()
                        .when(OrderPersonalInformationFields.DocumentoTipo, {
                            is: DOC_DNI_ID,
                            then: (s) => s.required("Requerido"),
                            otherwise: (s) => s.optional(),
                        }),
                    [OrderPersonalInformationFields.LastName]: yup
                        .string()
                        .when(OrderPersonalInformationFields.DocumentoTipo, {
                            is: DOC_DNI_ID,
                            then: (s) => s.required("Requerido"),
                            otherwise: (s) => s.optional(),
                        }),
                    [OrderPersonalInformationFields.Document]: yup.mixed().optional(),
                    [OrderPersonalInformationFields.Cuit]: yup
                        .string()
                        .when(OrderPersonalInformationFields.DocumentoTipo, {
                            is: DOC_DNI_ID,
                            then: (s) => s.optional(),
                            otherwise: (s) => s.required("Requerido"),
                        }),
                    [OrderPersonalInformationFields.RazonSocial]: yup
                        .string()
                        .nullable()
                        .when(OrderPersonalInformationFields.DocumentoTipo, {
                            is: DOC_DNI_ID,
                            then: (s) => s.optional(),
                            otherwise: (s) => s.required("Requerido"),
                        }),
                    [OrderPersonalInformationFields.AfipResponsableTipo]: yup
                        .number()
                        .nullable()
                        .optional(),
                    [OrderPersonalInformationFields.AreaCode]: yup
                        .string()
                        .required("Seleccioná un país.")
                        .test("country-code", "Seleccioná un país válido.", (val) => {
                            return Boolean(digitsOnly(val));
                        }),

                    [OrderPersonalInformationFields.PhoneNumber]: yup
                        .string()
                        .required("Ingresá código de área y número.")
                        .test("valid-phone", function (val) {
                            const phoneRaw = String(val ?? "");
                            const parent = this.parent as any;
                            const countryRaw = String(
                                parent?.[OrderPersonalInformationFields.AreaCode] ?? "",
                            );
                            const res = validateInternationalPhone(countryRaw, phoneRaw);

                            if (res.ok) return true;
                            return this.createError({ message: res.message });
                        }),
                }),
                [CheckoutFormTypeFields.DeliveryType]:
                    shippingOptions.length > 0 ? RequiredStringSchema : yup.string().optional(),
                [CheckoutFormTypeFields.DeliveryAddress]: yup
                    .object()
                    .shape({
                        [OrderDeliveryAddressFields.ZipCode]: RequiredStringSchema,
                    })
                    .when(CheckoutFormTypeFields.DeliveryType, ([deliveryType], schema) => {
                        const { optionId } = parseDeliveryValue(deliveryType as any);
                        const selectedOption = shippingOptions.find(
                            (opt) => opt[EntityWithIdFields.Id] === optionId,
                        );
                        const requiresAddress =
                            selectedOption?.[OrderShippingViewDTOFields.RequestAddress] ?? false;

                        if (deliveryType && requiresAddress) {
                            return yup.object({
                                [OrderDeliveryAddressFields.ZipCode]: RequiredStringSchema,
                                [OrderDeliveryAddressFields.Street]: RequiredStringSchema,
                                [OrderDeliveryAddressFields.StreetNumber]: yup
                                    .mixed()
                                    .nullable()
                                    .optional(),
                                [OrderDeliveryAddressFields.FloorApartment]: yup
                                    .mixed()
                                    .nullable()
                                    .optional(),
                                [OrderDeliveryAddressFields.ProvinceCode]: RequiredSelectSchema,
                                [OrderDeliveryAddressFields.LocalityDesc]: RequiredStringSchema,
                            });
                        }
                        return schema;
                    }),
                [CheckoutFormTypeFields.SameDeliveryAddress]: yup.boolean().optional(),
                [CheckoutFormTypeFields.ShippingAddress]: yup
                    .mixed()
                    .when(CheckoutFormTypeFields.SameDeliveryAddress, {
                        is: false,
                        then: () =>
                            yup.object({
                                [OrderDeliveryAddressFields.Street]: RequiredStringSchema,
                                [OrderDeliveryAddressFields.StreetNumber]: yup
                                    .mixed()
                                    .nullable()
                                    .optional(),
                                [OrderDeliveryAddressFields.FloorApartment]: yup
                                    .mixed()
                                    .nullable()
                                    .optional(),
                                [OrderDeliveryAddressFields.ProvinceCode]: RequiredSelectSchema,
                                [OrderDeliveryAddressFields.LocalityDesc]: RequiredStringSchema,
                            }),
                        otherwise: () => yup.mixed().optional(),
                    }),
                [CheckoutFormTypeFields.PaymentOptionId]:
                    (paymentOptions?.length ?? 0) > 0
                        ? yup.string().required("Seleccioná un método de pago.")
                        : yup.string().optional(),
            }),
        [shippingOptions, paymentOptions],
    );

    const methods = useForm<CheckoutFormType>({
        reValidateMode: "onBlur",
        defaultValues: {
            [CheckoutFormTypeFields.Subscribe]: true,
            [CheckoutFormTypeFields.SameDeliveryAddress]: true,
            [CheckoutFormTypeFields.CouponStatus]: {
                loading: false,
                error: null,
                success: false,
                appliedDiscount: null,
            },
        },
        // @ts-ignore
        resolver: yupResolver(checkoutFormTypeSchema),
    });
    const nameZipCode: "deliveryAddress.codigoPostal" = `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}`;
    const watchZipCode = methods.watch(nameZipCode);

    const nameMail: "personalInformation.mail" = `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}`;
    const watchMail = methods.watch(nameMail);

    const watchDeliveryType = methods.watch(CheckoutFormTypeFields.DeliveryType);

    const { optionId: selectedOptionId, branchId: selectedBranchId } = useMemo(
        () => parseDeliveryValue(watchDeliveryType as any),
        [watchDeliveryType],
    );

    const isHomeDelivery = useMemo(
        () => isHomeDeliveryByType(selectedOptionId || undefined, shippingOptions),
        [selectedOptionId, shippingOptions],
    );

    const watchPaymentOptionId = methods.watch(CheckoutFormTypeFields.PaymentOptionId);

    const selectedPaymentProviderCode = useMemo<number | null>(() => {
        if (!watchPaymentOptionId || !paymentOptions.length) return null;
        const opt = paymentOptions.find(
            (o: any) => String(o?.id ?? "") === String(watchPaymentOptionId),
        );
        return opt != null ? Number(opt[OrderPaymentOptionViewDTOFields.ProviderCode] ?? 0) : null;
    }, [watchPaymentOptionId, paymentOptions]);

    useEffect(() => {
        setPaymentData(null);
    }, [watchPaymentOptionId]);

    const setPaymentOptionId = (value: string) => {
        methods.setValue(CheckoutFormTypeFields.PaymentOptionId as any, value, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
    };

    const watchCouponStatus = methods.watch(CheckoutFormTypeFields.CouponStatus);

    const MIN_PURCHASE_AMOUNT = window.APP_CONFIG[AppConfigFields.MinimumPurchaseAmount] ?? 150000;

    const minPurchaseSubtotal = useMemo(() => {
        const calc = watchCouponStatus?.calculationData;
        if (calc) {
            const subtotal = Number(calc[OrderCalculationViewFields.Subtotal] ?? 0);
            const discount = Number(calc[OrderCalculationViewFields.ProductsDiscount] ?? 0);
            return Math.max(0, subtotal - discount);
        }

        const raw = cartItems.reduce(
            (sum, item) => sum + Number(item.price ?? 0) * Number(item.quantity ?? 0),
            0,
        );
        return Math.max(0, raw);
    }, [watchCouponStatus?.calculationData, cartItems]);

    const isCartEmpty = cartItems.length === 0;
    const isCheckoutBlocked = isCartEmpty || minPurchaseSubtotal < MIN_PURCHASE_AMOUNT;

    const minPurchaseMissing = useMemo(() => {
        if (isCartEmpty) return MIN_PURCHASE_AMOUNT;
        return Math.max(0, MIN_PURCHASE_AMOUNT - minPurchaseSubtotal);
    }, [isCartEmpty, MIN_PURCHASE_AMOUNT, minPurchaseSubtotal]);

    const formatPrice = (price: number) => {
        return price === 0
            ? "Gratis"
            : `$${price.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const deliveryOptions = useMemo(() => {
        const homeDeliveryOptions = shippingOptions
            .filter((option) => option[OrderShippingViewDTOFields.RequestAddress] === true)
            .map((option) => {
                const { cost, isToBeAgreed } = resolveShippingCost(option);

                return {
                    value: makeDeliveryValue(option[EntityWithIdFields.Id], null),
                    title: option[OrderShippingViewDTOFields.OptionName],
                    description: option[OrderShippingViewDTOFields.FormattedDescription],
                    price: isToBeAgreed ? "A convenir" : formatPrice(cost ?? 0),
                    priceNumber: cost,
                    isToBeAgreed,
                    summary: `${option[OrderShippingViewDTOFields.OptionName]} - ${
                        isToBeAgreed ? "A convenir" : formatPrice(cost ?? 0)
                    }`,
                };
            });

        const pickupOptions = shippingOptions
            .filter((option) => option[OrderShippingViewDTOFields.RequestAddress] === false)
            .flatMap((option) => {
                const { cost, isToBeAgreed } = resolveShippingCost(option);
                const optionId = option[EntityWithIdFields.Id];
                const branches = option[OrderShippingViewDTOFields.Branches] ?? [];

                if (!branches || branches.length === 0) {
                    return [
                        {
                            value: makeDeliveryValue(optionId, null),
                            title: option[OrderShippingViewDTOFields.OptionName],
                            description: option[OrderShippingViewDTOFields.FormattedDescription],
                            price: isToBeAgreed ? "A convenir" : formatPrice(cost ?? 0),
                            priceNumber: cost,
                            isToBeAgreed,
                            summary: `${option[OrderShippingViewDTOFields.OptionName]} - ${isToBeAgreed ? "A convenir" : formatPrice(cost ?? 0)}`,
                        },
                    ];
                }

                return branches.map((b: any, idx: number) => {
                    const branchId = getBranchId(b, idx);
                    const branchName = getBranchName(b);
                    const branchAddress = getBranchAddress(b);

                    const title = `${option[OrderShippingViewDTOFields.OptionName]} - ${branchName}`;
                    const description =
                        branchAddress ?? option[OrderShippingViewDTOFields.FormattedDescription];
                    const priceText = isToBeAgreed ? "A convenir" : formatPrice(cost ?? 0);

                    return {
                        value: makeDeliveryValue(optionId, branchId),
                        title,
                        description,
                        price: priceText,
                        priceNumber: cost,
                        isToBeAgreed,
                        summary: `${title} - ${priceText}`,
                    };
                });
            });

        return [
            ...(homeDeliveryOptions.length > 0
                ? [
                      {
                          Icon: TruckIcon,
                          title: "Entrega a Domicilio",
                          options: homeDeliveryOptions,
                      },
                  ]
                : []),
            ...(pickupOptions.length > 0
                ? [
                      {
                          Icon: MapPinIcon,
                          title: "Retirar por Local",
                          options: pickupOptions,
                      },
                  ]
                : []),
        ];
    }, [shippingOptions]);

    const onCancelZipCode = () => {
        setCurrentStep(CheckoutProcessStep.ZipCode);
        setConfirmedZipCode(false);
        methods.setValue(CheckoutFormTypeFields.DeliveryType, undefined);
    };

    const changeDeliveryZip = async (newZip: string) => {
        methods.setValue(
            `${CheckoutFormTypeFields.ShippingAddress}.${OrderDeliveryAddressFields.ZipCode}` as any,
            newZip,
            { shouldDirty: false, shouldTouch: false, shouldValidate: false },
        );
        if (!newZip) return;
        try {
            const requestData: ShippingCalculationRequest = {
                codigoPostal: newZip,
                productosOrden: cartItems.map((item) => ({
                    idProducto: item.idProduct,
                    idProductoVariante: item.idProductVariant,
                    cantidad: item.quantity,
                })),
            };
            const options = await HttpShippings.calculateShippingOptions(requestData);
            setShippingOptions(options);
            methods.setValue(CheckoutFormTypeFields.DeliveryType, undefined);
        } catch (error) {
            console.error("Error calculating shipping for delivery zip:", error);
            enqueueSnackbar("No se pudieron calcular los envíos para ese código postal.", {
                variant: "warning",
            });
        }
    };

    const getProductsFromCartItems = (): OrderProduct[] =>
        cartItems.map((item) => {
            return {
                [OrderProductFields.ProductId]: item.idProduct,
                [OrderProductFields.ProductVariantId]: item.idProductVariant,
                [OrderProductFields.Quantity]: item.quantity,
            } as OrderProduct;
        });

    const getOrderCheckoutUpdateFromForm = (data: CheckoutFormType | undefined = undefined) => {
        if (!data) {
            data = {
                [CheckoutFormTypeFields.Subscribe]: methods.getValues(
                    CheckoutFormTypeFields.Subscribe,
                ),
                [CheckoutFormTypeFields.CouponStatus]: methods.getValues(
                    CheckoutFormTypeFields.CouponStatus,
                ),
                [CheckoutFormTypeFields.PersonalInformation]: {
                    ...(methods.getValues(CheckoutFormTypeFields.PersonalInformation) || {}),
                    [OrderPersonalInformationFields.Email]: methods.getValues(nameMail),
                } as OrderPersonalInformation,
                [CheckoutFormTypeFields.DeliveryAddress]: {
                    ...(methods.getValues(CheckoutFormTypeFields.DeliveryAddress) || {}),
                    [OrderDeliveryAddressFields.ZipCode]: methods.getValues(nameZipCode),
                } as OrderDeliveryAddress,
                [CheckoutFormTypeFields.DeliveryType]:
                    methods.getValues(CheckoutFormTypeFields.DeliveryType) || "",
                [CheckoutFormTypeFields.PaymentOptionId]:
                    methods.getValues(CheckoutFormTypeFields.PaymentOptionId) || undefined,
            } as CheckoutFormType;
        }

        const ac = digitsOnly(
            data[CheckoutFormTypeFields.PersonalInformation][
                OrderPersonalInformationFields.AreaCode
            ]?.toString(),
        );
        const pn = digitsOnly(
            data[CheckoutFormTypeFields.PersonalInformation][
                OrderPersonalInformationFields.PhoneNumber
            ]?.toString(),
        );

        data[CheckoutFormTypeFields.PersonalInformation][OrderPersonalInformationFields.AreaCode] =
            ac || null;
        data[CheckoutFormTypeFields.PersonalInformation][
            OrderPersonalInformationFields.PhoneNumber
        ] = pn || null;

        data[CheckoutFormTypeFields.PersonalInformation][OrderPersonalInformationFields.Email] =
            methods.getValues(nameMail);

        data[CheckoutFormTypeFields.DeliveryAddress][OrderDeliveryAddressFields.ZipCode] =
            methods.getValues(nameZipCode);

        data[CheckoutFormTypeFields.PersonalInformation][OrderPersonalInformationFields.Document] =
            data[CheckoutFormTypeFields.PersonalInformation][
                OrderPersonalInformationFields.Document
            ]?.toString();

        data[CheckoutFormTypeFields.PersonalInformation][OrderPersonalInformationFields.AreaCode] =
            data[CheckoutFormTypeFields.PersonalInformation][
                OrderPersonalInformationFields.AreaCode
            ]?.toString();

        data[CheckoutFormTypeFields.PersonalInformation][
            OrderPersonalInformationFields.PhoneNumber
        ] =
            data[CheckoutFormTypeFields.PersonalInformation][
                OrderPersonalInformationFields.PhoneNumber
            ]?.toString();

        const { optionId: deliveryOptionId, branchId: deliveryBranchId } = parseDeliveryValue(
            data?.[CheckoutFormTypeFields.DeliveryType] as any,
        );

        const selectedOption = shippingOptions.find(
            (opt) => opt[EntityWithIdFields.Id] === deliveryOptionId,
        );
        const requiresAddress =
            selectedOption?.[OrderShippingViewDTOFields.RequestAddress] ?? false;
        const resolved = resolveShippingCost(selectedOption);
        const shippingCost = resolved.cost;
        const paymentOptionId = data?.[CheckoutFormTypeFields.PaymentOptionId];

        const billingAddr = data[CheckoutFormTypeFields.DeliveryAddress];
        const order: OrderCheckoutUpdate = {
            [OrderCheckoutFields.PersonalInformation]: {
                ...data[CheckoutFormTypeFields.PersonalInformation],
                [OrderDeliveryAddressFields.Street]:
                    billingAddr?.[OrderDeliveryAddressFields.Street] ?? null,
                [OrderDeliveryAddressFields.StreetNumber]:
                    billingAddr?.[OrderDeliveryAddressFields.StreetNumber] ?? null,
                [OrderDeliveryAddressFields.FloorApartment]:
                    billingAddr?.[OrderDeliveryAddressFields.FloorApartment] ?? null,
                [OrderDeliveryAddressFields.ZipCode]:
                    billingAddr?.[OrderDeliveryAddressFields.ZipCode] ?? null,
                [OrderDeliveryAddressFields.ProvinceCode]:
                    billingAddr?.[OrderDeliveryAddressFields.ProvinceCode] ?? null,
                [OrderDeliveryAddressFields.LocalityDesc]:
                    billingAddr?.[OrderDeliveryAddressFields.LocalityDesc] ?? null,
            },
            [OrderCheckoutFields.Products]: getProductsFromCartItems(),
            [OrderCheckoutFields.Subscribe]: data[CheckoutFormTypeFields.Subscribe],
            [OrderCheckoutFields.ShippingOptionId]: deliveryOptionId || undefined,
            [OrderCheckoutFields.ShippingBranchId]: deliveryBranchId ?? null,
            [OrderCheckoutFields.DeliveryAddress]: requiresAddress
                ? data[CheckoutFormTypeFields.SameDeliveryAddress] === false
                    ? data[CheckoutFormTypeFields.ShippingAddress]
                    : data[CheckoutFormTypeFields.DeliveryAddress]
                : undefined,
            [OrderCheckoutFields.ShippingCost]: shippingCost,
            [OrderCheckoutFields.CouponName]:
                data[CheckoutFormTypeFields.CouponStatus]?.appliedDiscount?.[
                    AppliedCouponDiscountFields.Name
                ] || null,
            [OrderCheckoutFields.PaymentOptionId]: paymentOptionId
                ? String(paymentOptionId)
                : undefined,
            [OrderCheckoutFields.PaymentData]: paymentDataRef.current ?? null,
            [OrderCheckoutFields.CamposAdicionales]:
                lstCamposAdicionales.length > 0
                    ? lstCamposAdicionales.map(
                          (c: SuscripcionCampoAdicionalVista): OrdenCampoAdicionalValorDTO => ({
                              uniIdSuscripcionCampoAdicional: c.uniIdSuscripcionCampoAdicional,
                              vchValor:
                                  camposAdicionalesValoresRef.current[
                                      c.uniIdSuscripcionCampoAdicional
                                  ] ?? null,
                          }),
                      )
                    : null,
        };

        return { ...order };
    };

    const createOrUpdateOrder = async (
        data: CheckoutFormType | undefined = undefined,
        showError: boolean = false,
    ) => {
        const currentMail = methods.getValues(nameMail);
        const currentZip = methods.getValues(nameZipCode);

        const currentDeliveryValue = methods.getValues(CheckoutFormTypeFields.DeliveryType) as any;
        const { optionId: currentDeliveryType, branchId: currentDeliveryBranchId } =
            parseDeliveryValue(currentDeliveryValue);
        let currentSelectedShippingCost: number | null = null;

        if (currentDeliveryType) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === currentDeliveryType,
            );
            if (selectedOption) {
                const resolved = resolveShippingCost(selectedOption);
                currentSelectedShippingCost = resolved.cost;
            }
        }

        const mailOk = RequiredMailSchema.isValidSync(currentMail || "");
        const zipOk = RequiredStringSchema.isValidSync(currentZip || "");
        const deliveryOk =
            !!currentDeliveryType &&
            currentSelectedShippingCost !== null &&
            currentSelectedShippingCost !== undefined;

        if (!mailOk || !zipOk || !deliveryOk) return null;

        const existingGuid = orderGuidRef.current;

        if (!existingGuid) {
            if (creatingOrderRef.current) return null;
            creatingOrderRef.current = true;

            await methods.trigger(CheckoutFormTypeFields.DeliveryType);

            try {
                const deliveryValue = methods.getValues(CheckoutFormTypeFields.DeliveryType) as any;
                const { optionId: deliveryOptionId, branchId: deliveryBranchId } =
                    parseDeliveryValue(deliveryValue);

                let selectedShippingCost: number | null = null;
                if (deliveryOptionId) {
                    const selectedOption = shippingOptions.find(
                        (opt) => opt[EntityWithIdFields.Id] === deliveryOptionId,
                    );
                    selectedShippingCost = resolveShippingCost(selectedOption).cost;
                }

                if (
                    !deliveryOptionId ||
                    selectedShippingCost === null ||
                    selectedShippingCost === undefined
                )
                    return null;

                const orderToCreate: OrderCheckoutCreate = {
                    [OrderCheckoutCreateFields.Email]: currentMail,
                    [OrderCheckoutCreateFields.ZipCode]: currentZip,
                    [OrderCheckoutCreateFields.IdShippingOption]: deliveryOptionId,
                    [OrderCheckoutCreateFields.ShippingBranchId]: deliveryBranchId ?? null,
                    [OrderCheckoutCreateFields.ShippingOptionPrice]: selectedShippingCost,
                    [OrderCheckoutCreateFields.Products]: getProductsFromCartItems(),
                };

                const response = await HttpOrder.createOrder(orderToCreate);

                if (response[BaseResponseFields.HasError]) {
                    if (showError) {
                        enqueueSnackbar(
                            response[BaseResponseFields.ErrorDescription] ||
                                "No se pudo crear la orden",
                            { variant: "error" },
                        );
                    } else {
                        console.error(
                            "Error creating order:",
                            response[BaseResponseFields.ErrorDescription],
                        );
                    }
                    return null;
                }

                const newGuid = response[BaseResponseFields.Data];

                setOrderGuidSafe(newGuid);

                const newUrl =
                    window.location.protocol +
                    "//" +
                    window.location.host +
                    window.location.pathname;

                window.history.pushState({ path: newUrl }, "", newUrl);

                const currentPaymentOptionIdRaw = methods.getValues(
                    CheckoutFormTypeFields.PaymentOptionId,
                ) as any;
                const currentPaymentOptionId = currentPaymentOptionIdRaw
                    ? String(currentPaymentOptionIdRaw)
                    : null;

                lastSyncedMailRef.current = currentMail;
                lastSyncedZipRef.current = currentZip;
                lastSyncedDeliveryTypeRef.current = deliveryValue;
                lastSyncedDeliveryBranchRef.current = currentDeliveryBranchId ?? null;
                lastSyncedShippingCostRef.current = selectedShippingCost;
                lastSyncedPaymentOptionRef.current = currentPaymentOptionId;

                if (data) {
                    const orderToUpdate: OrderCheckoutUpdate = getOrderCheckoutUpdateFromForm(data);
                    const respUpdate = await HttpOrder.updateOrder(newGuid, orderToUpdate);

                    if (respUpdate[BaseResponseFields.HasError]) {
                        if (showError) {
                            enqueueSnackbar(
                                respUpdate[BaseResponseFields.ErrorDescription] ||
                                    "No se pudo actualizar la orden",
                                { variant: "error" },
                            );
                        } else {
                            console.error(
                                "Error updating order:",
                                respUpdate[BaseResponseFields.ErrorDescription],
                            );
                        }
                        return null;
                    }

                    lastSyncedMailRef.current = currentMail;
                    lastSyncedZipRef.current = currentZip;
                    lastSyncedDeliveryTypeRef.current = deliveryValue;
                    lastSyncedDeliveryBranchRef.current = currentDeliveryBranchId ?? null;
                    lastSyncedShippingCostRef.current = selectedShippingCost;
                    lastSyncedPaymentOptionRef.current = currentPaymentOptionId;
                }

                return newGuid;
            } finally {
                creatingOrderRef.current = false;
            }
        }

        const orderToUpdate: OrderCheckoutUpdate = getOrderCheckoutUpdateFromForm(data);
        const response = await HttpOrder.updateOrder(existingGuid, orderToUpdate);

        if (response[BaseResponseFields.HasError]) {
            if (showError) {
                enqueueSnackbar(
                    response[BaseResponseFields.ErrorDescription] ||
                        "No se pudo actualizar la orden",
                    { variant: "error" },
                );
                return null;
            } else {
                console.error(
                    "Error updating order:",
                    response[BaseResponseFields.ErrorDescription],
                );
                return existingGuid;
            }
        }

        lastSyncedMailRef.current = currentMail;
        lastSyncedZipRef.current = currentZip;

        const currDeliveryValueRaw = methods.getValues(CheckoutFormTypeFields.DeliveryType) as any;
        const currDeliveryValue = currDeliveryValueRaw ? String(currDeliveryValueRaw) : null;

        const currentPaymentOptionIdRaw = methods.getValues(
            CheckoutFormTypeFields.PaymentOptionId,
        ) as any;
        const currentPaymentOptionId = currentPaymentOptionIdRaw
            ? String(currentPaymentOptionIdRaw)
            : null;

        lastSyncedDeliveryTypeRef.current = currDeliveryValue;
        lastSyncedShippingCostRef.current = currentSelectedShippingCost;
        lastSyncedDeliveryBranchRef.current = currentDeliveryBranchId ?? null;
        lastSyncedPaymentOptionRef.current = currentPaymentOptionId;

        return existingGuid;
    };

    const onSubmitZip = async () => {
        setIsLoadingZipCode(true);

        // Solo el email es obligatorio; el CP es opcional
        const isMailValid = await methods.trigger(nameMail);

        if (!isMailValid) {
            setIsLoadingZipCode(false);
            return;
        }

        try {
            const zipCodeValue = methods.getValues(nameZipCode);

            if (zipCodeValue) {
                try {
                    const requestData: ShippingCalculationRequest = {
                        codigoPostal: zipCodeValue,
                        productosOrden: cartItems.map((item) => ({
                            idProducto: item.idProduct,
                            idProductoVariante: item.idProductVariant,
                            cantidad: item.quantity,
                        })),
                    };
                    const options = await HttpShippings.calculateShippingOptions(requestData);
                    setShippingOptions(options);
                } catch (error) {
                    console.error("Error calculating shipping:", error);
                    enqueueSnackbar("No se pudieron calcular los envíos para ese código postal.", {
                        variant: "warning",
                    });
                }
            }

            setConfirmedZipCode(true);
            setCurrentStep(CheckoutProcessStep.CustomerDetails);
            methods.setValue(CheckoutFormTypeFields.DeliveryType, undefined);
            setContextShippingOptionId(null);
            setContextShippingOptionBranchId(null);
        } finally {
            setIsLoadingZipCode(false);
        }
    };

    const onSubmitCustomerDetail = async (data: CheckoutFormType) => {
        setIsLoadingCustomerDetail(true);

        try {
            const isValidDelivery = await methods.trigger(CheckoutFormTypeFields.DeliveryType);
            const isValidPayment = await methods.trigger(CheckoutFormTypeFields.PaymentOptionId);

            if (!isValidDelivery || !isValidPayment) {
                return;
            }

            const paymentOptId = data?.[
                CheckoutFormTypeFields.PaymentOptionId as keyof CheckoutFormType
            ] as string | undefined;
            const currentPaymentOpt = paymentOptions.find(
                (o: any) => String(o?.id ?? "") === String(paymentOptId ?? ""),
            );
            const currentProviderCode = Number(
                currentPaymentOpt?.[OrderPaymentOptionViewDTOFields.ProviderCode] ?? 0,
            );

            if (currentProviderCode === PROVEEDOR_PAGO.TARJETA) {
                if (
                    !paymentDataRef.current?.tarjetaNombreTitular ||
                    !paymentDataRef.current?.tarjetaFechaVto ||
                    !paymentDataRef.current?.codTarjetaTipo ||
                    !paymentDataRef.current?.tarjetaNumero
                ) {
                    enqueueSnackbar("Completá los datos de la tarjeta.", { variant: "error" });
                    return;
                }

                const tipoTarjetaSeleccionado = cardTypes.find(
                    (ct) => ct.id === paymentDataRef.current?.codTarjetaTipo,
                );
                if (
                    tipoTarjetaSeleccionado &&
                    !cardNumberMatchesSelectedBrand(
                        paymentDataRef.current.tarjetaNumero,
                        tipoTarjetaSeleccionado.descripcion,
                    )
                ) {
                    enqueueSnackbar(
                        `El número de tarjeta no corresponde a ${tipoTarjetaSeleccionado.descripcion}. Revisalo antes de continuar.`,
                        { variant: "error" },
                    );
                    return;
                }
            } else if (currentProviderCode === PROVEEDOR_PAGO.CBU) {
                if (
                    !paymentDataRef.current?.cbu ||
                    String(paymentDataRef.current.cbu).length !== 22
                ) {
                    enqueueSnackbar("Ingresá un CBU válido de 22 dígitos.", { variant: "error" });
                    return;
                }
            }

            const primerCampoFaltante = lstCamposAdicionales
                .filter((c: SuscripcionCampoAdicionalVista) => c.bitRequerido)
                .find((c: SuscripcionCampoAdicionalVista) => {
                    const val =
                        camposAdicionalesValoresRef.current[c.uniIdSuscripcionCampoAdicional];
                    if (c.tinCodSuscripcionCampoAdicionalTipo === TIPO_CAMPO_ADICIONAL.CHECKBOX) {
                        return val !== "true";
                    }
                    return !val?.trim();
                });
            if (primerCampoFaltante) {
                enqueueSnackbar(`Completá el campo "${primerCampoFaltante.vchLabel}".`, {
                    variant: "error",
                });
                return;
            }

            const guid = await createOrUpdateOrder(data, true);

            if (!!guid) {
                setCurrentStep(CheckoutProcessStep.PaymentAndConfirmation);
            }
        } finally {
            setIsLoadingCustomerDetail(false);
        }
    };

    const clearCheckoutUrl = () => {
        const newUrl =
            window.location.protocol + "//" + window.location.host + window.location.pathname;

        window.history.pushState({ path: newUrl }, "", newUrl);
    };

    const onHandleSubmit = async (data: CheckoutFormType) => {
        const guidFromRef = orderGuidRef.current;
        if (!guidFromRef) {
            enqueueSnackbar("No se encontró la orden en curso. Volvé a intentar.", {
                variant: "error",
            });
            return;
        }

        setIsLoadingOrder(true);

        try {
            const guid = await createOrUpdateOrder(data, true);

            if (!guid) {
                setIsLoadingOrder(false);
                return;
            }

            let selectedShippingCost: number | null = null;
            const { optionId: deliveryTypeId, branchId: deliveryBranchId } = parseDeliveryValue(
                data[CheckoutFormTypeFields.DeliveryType] as any,
            );

            if (deliveryTypeId) {
                const selectedOption = shippingOptions.find(
                    (opt) => opt[EntityWithIdFields.Id] === deliveryTypeId,
                );
                if (selectedOption) {
                    const resolved = resolveShippingCost(selectedOption);
                    selectedShippingCost = resolved.cost;
                }
            }

            const order: OrderCheckoutFinish = {
                ...getOrderCheckoutUpdateFromForm(data),
                [OrderCheckoutFinishFields.Name]:
                    data[CheckoutFormTypeFields.CouponStatus]?.appliedDiscount?.[
                        AppliedCouponDiscountFields.Name
                    ],
                [OrderCheckoutFields.ShippingOptionId]: deliveryTypeId || undefined,
                [OrderCheckoutFields.ShippingBranchId]: deliveryBranchId ?? null,
                [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
            };

            const currGuid = orderGuidRef.current;
            if (!currGuid) {
                enqueueSnackbar("No se encontró la orden en curso. Volvé a intentar.", {
                    variant: "error",
                });
                return;
            }

            const response = await HttpOrder.completeCheckout(currGuid, order);

            if (response[BaseResponseFields.HasError]) {
                const respData = response[BaseResponseFields.Data];

                if (respData?.listaProductosConStockInsuficiente?.length) {
                    setProcessStatus({
                        type: CheckoutProcessStatusType.RequiresStockResolution,
                        insufficientStockProducts: respData.listaProductosConStockInsuficiente,
                        description: "Algunos productos no tienen stock suficiente.",
                    });

                    return;
                }

                setProcessStatus({
                    type: CheckoutProcessStatusType.FinishedWithError,
                    description:
                        response[BaseResponseFields.ErrorDescription] ||
                        "Ocurrió un error al completar la compra",
                });

                setCurrentStep(CheckoutProcessStep.Finished);
                clearCheckoutUrl();
                return;
            }

            const respData = response[BaseResponseFields.Data];

            const mpRedirectUrl = respData?.mercadoPagoRedirectUrl;
            const mpPreferenceId = respData?.mercadoPagoPreferenceId;

            if (mpRedirectUrl || mpPreferenceId) {
                if (mpRedirectUrl) {
                    isRedirectingToMpRef.current = true;

                    window.location.assign(mpRedirectUrl);
                    return;
                }

                setProcessStatus({
                    type: CheckoutProcessStatusType.FinishedWithError,
                    description: "No se pudo iniciar Mercado Pago (faltó URL de redirección).",
                });
                setCurrentStep(CheckoutProcessStep.Finished);
                clearCheckoutUrl();
                return;
            }

            const numeroOrden = response[BaseResponseFields.Data]?.codigo;

            const p = data[CheckoutFormTypeFields.PersonalInformation];
            const d = data[CheckoutFormTypeFields.DeliveryAddress];

            const customerName =
                `${p[OrderPersonalInformationFields.FirstName] ?? ""} ${p[OrderPersonalInformationFields.LastName] ?? ""}`.trim();

            const customerPhone = formatInternationalPhoneForSummary(
                p[OrderPersonalInformationFields.AreaCode],
                p[OrderPersonalInformationFields.PhoneNumber],
            );

            const deliveryAddress = d?.[OrderDeliveryAddressFields.Street]
                ? `${d[OrderDeliveryAddressFields.Street]} ${d[OrderDeliveryAddressFields.StreetNumber] ?? ""}, ${d[OrderDeliveryAddressFields.LocalityDesc] ?? ""}`
                : null;

            const snapshotItems = cartItems.map((item) => ({
                name: item.name,
                image: item.image,
                quantity: item.quantity,
                price: item.price,
                attributes: item.attributes ?? [],
            }));

            setProcessStatus({
                type: CheckoutProcessStatusType.FinishedSuccessfully,
                orderNumber: numeroOrden,
                description: `En los próximos minutos recibirás un correo con los detalles.`,
                customerEmail: p[OrderPersonalInformationFields.Email],
                customerName,
                customerPhone,
                deliveryAddress,
                zipCode: d?.[OrderDeliveryAddressFields.ZipCode] ?? "",
                items: snapshotItems,
            });

            clearCart();

            clearRootCartIfMpFailureResume(currGuid);

            setCurrentStep(CheckoutProcessStep.Finished);
            clearCheckoutUrl();
        } catch (e) {
            setProcessStatus({
                type: CheckoutProcessStatusType.FinishedWithError,
                description: "Ocurrió un error al completar la compra",
            });

            setCurrentStep(CheckoutProcessStep.Finished);
            clearCheckoutUrl();
        } finally {
            if (!isRedirectingToMpRef.current) {
                setIsLoadingOrder(false);
            }
        }
    };

    useEffect(() => {
        if (contextZipCode && cartItems.length > 0) {
            methods.setValue(nameZipCode as any, contextZipCode);
            setConfirmedZipCode(true);
            setCurrentStep(CheckoutProcessStep.CustomerDetails);

            setIsLoadingZipCode(true);
            const requestData: ShippingCalculationRequest = {
                codigoPostal: contextZipCode,
                productosOrden: cartItems.map((item) => ({
                    idProducto: item.idProduct,
                    idProductoVariante: item.idProductVariant,
                    cantidad: item.quantity,
                })),
            };

            HttpShippings.calculateShippingOptions(requestData)
                .then((options) => {
                    setShippingOptions(options);
                })
                .catch((error) => {
                    console.error("Error calculating shipping:", error);
                    enqueueSnackbar(
                        "Hubo un error al calcular los envíos. Verifique el código postal.",
                        { variant: "error" },
                    );
                })
                .finally(() => {
                    setIsLoadingZipCode(false);
                });
        }
    }, []);

    useEffect(() => {
        if (contextShippingOptionId && shippingOptions.length > 0) {
            const raw = makeDeliveryValue(
                contextShippingOptionId,
                contextShippingOptionBranchId ?? null,
            );
            methods.setValue(CheckoutFormTypeFields.DeliveryType as any, raw);

            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === contextShippingOptionId,
            );
            if (selectedOption) {
                const resolved = resolveShippingCost(selectedOption);
                const selectedShippingCost = resolved.cost;

                const products: OrderProduct[] = cartItems.map((item) => ({
                    [OrderProductFields.ProductId]: item.idProduct,
                    [OrderProductFields.ProductVariantId]: item.idProductVariant,
                    [OrderProductFields.Quantity]: item.quantity,
                }));

                const calculationData: OrderCalculationRequest = {
                    [OrderCalculationRequestFields.Products]: products,
                    [OrderCalculationRequestFields.CouponName]: null,
                    [OrderCalculationRequestFields.ShippingOptionId]: contextShippingOptionId,
                    [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
                };

                HttpOrder.calculate(calculationData)
                    .then((response) => {
                        if (!response[BaseResponseFields.HasError]) {
                            methods.setValue(CheckoutFormTypeFields.CouponStatus, {
                                loading: false,
                                error: null,
                                success: false,
                                appliedDiscount: null,
                                calculationData: response[BaseResponseFields.Data],
                            });
                        }
                    })
                    .catch((error) => {
                        console.error("Error calculating order:", error);
                    });
            }
        }
    }, [contextShippingOptionId, contextShippingOptionBranchId, shippingOptions]);

    const setDeliveryType = (value: string) => {
        methods.setValue(CheckoutFormTypeFields.DeliveryType as any, value, {
            shouldValidate: true,
        });

        const { optionId, branchId } = parseDeliveryValue(value);

        setContextShippingOptionId(optionId);
        setContextShippingOptionBranchId(branchId);
    };

    // Auto-seleccionar cuando hay una sola opción de envío disponible
    useEffect(() => {
        if (!confirmedZipCode) return;
        const allOptions = deliveryOptions.flatMap((group) => group.options);
        if (allOptions.length !== 1) return;
        const current = methods.getValues(CheckoutFormTypeFields.DeliveryType);
        if (current) return;
        setDeliveryType(allOptions[0].value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deliveryOptions, confirmedZipCode]);

    const setCouponStatus = (status: CouponStatus) =>
        methods.setValue(CheckoutFormTypeFields.CouponStatus, status);

    const applyCoupon = (nameCoupon: string) => {
        setCouponStatus({
            loading: true,
            error: null,
            success: false,
            appliedDiscount: null,
            calculationData: null,
        });

        let selectedShippingCost: number | null = null;

        if (selectedOptionId) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === selectedOptionId,
            );
            const resolved = resolveShippingCost(selectedOption);
            selectedShippingCost = resolved.cost;
        }

        const data: OrderCalculationRequest = {
            [OrderCalculationRequestFields.Products]: getProductsFromCartItems(),
            [OrderCalculationRequestFields.CouponName]: nameCoupon,
            [OrderCalculationRequestFields.ShippingOptionId]: selectedOptionId || null,
            [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
        };

        HttpOrder.calculate(data)
            .then((response) => {
                if (response[BaseResponseFields.HasError]) {
                    const couponError =
                        response[BaseResponseFields.ErrorDescription] ||
                        "Cupón inválido o no aplicable";
                    setCouponStatus({
                        loading: false,
                        error: couponError,
                        success: false,
                        appliedDiscount: null,
                        calculationData: null,
                    });
                } else {
                    const calculationData = response[BaseResponseFields.Data];
                    const appliedDiscount: AppliedCouponDiscount = {
                        [AppliedCouponDiscountFields.Id]:
                            calculationData[EntityWithIdFields.Id] || "",
                        [AppliedCouponDiscountFields.Name]: nameCoupon,
                        [AppliedCouponDiscountFields.DiscountTotal]:
                            calculationData[OrderCalculationViewFields.Total],
                        [AppliedCouponDiscountFields.OriginalTotal]:
                            calculationData[OrderCalculationViewFields.Subtotal],
                        [AppliedCouponDiscountFields.FreeShipping]:
                            calculationData[OrderCalculationViewFields.ShippingDiscount] > 0,
                    };

                    setCouponStatus({
                        loading: false,
                        error: null,
                        success: true,
                        appliedDiscount,
                        calculationData,
                    });
                    setCouponCode("");
                    enqueueSnackbar("Cupón aplicado correctamente!", { variant: "success" });
                }
            })
            .catch((error) => {
                const couponError = error?.message || "Cupón inválido o no aplicable";
                setCouponStatus({
                    loading: false,
                    error: couponError,
                    success: false,
                    appliedDiscount: null,
                    calculationData: null,
                });
            });
    };

    const recalculateOrder = async (productsOverride?: OrderProduct[]) => {
        let selectedShippingCost: number | null = null;

        if (selectedOptionId) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === selectedOptionId,
            );
            const resolved = resolveShippingCost(selectedOption);
            selectedShippingCost = resolved.cost;
        }

        const data: OrderCalculationRequest = {
            [OrderCalculationRequestFields.Products]:
                productsOverride ?? getProductsFromCartItems(),
            [OrderCalculationRequestFields.CouponName]:
                watchCouponStatus?.appliedDiscount?.[AppliedCouponDiscountFields.Name] || null,
            [OrderCalculationRequestFields.ShippingOptionId]: selectedOptionId || null,
            [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
        };

        const response = await HttpOrder.calculate(data);

        if (response[BaseResponseFields.HasError]) {
            return null;
        }

        const calculationData = response[BaseResponseFields.Data];

        if (watchCouponStatus?.appliedDiscount) {
            const appliedDiscount: AppliedCouponDiscount = {
                [AppliedCouponDiscountFields.Id]:
                    calculationData[EntityWithIdFields.Id] ||
                    watchCouponStatus.appliedDiscount[AppliedCouponDiscountFields.Id],
                [AppliedCouponDiscountFields.Name]:
                    watchCouponStatus.appliedDiscount[AppliedCouponDiscountFields.Name],
                [AppliedCouponDiscountFields.DiscountTotal]:
                    calculationData[OrderCalculationViewFields.Total],
                [AppliedCouponDiscountFields.OriginalTotal]:
                    calculationData[OrderCalculationViewFields.Subtotal],
                [AppliedCouponDiscountFields.FreeShipping]:
                    (calculationData[OrderCalculationViewFields.ShippingDiscount] ?? 0) > 0,
            };

            methods.setValue(CheckoutFormTypeFields.CouponStatus, {
                ...watchCouponStatus,
                loading: false,
                error: null,
                success: true,
                appliedDiscount,
                calculationData,
            });
        } else {
            methods.setValue(CheckoutFormTypeFields.CouponStatus, {
                loading: false,
                error: null,
                success: false,
                appliedDiscount: null,
                calculationData,
            });
        }

        return calculationData;
    };

    const recalculateOrderWithoutCoupon = async () => {
        let selectedShippingCost: number | null = null;

        if (selectedOptionId) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === selectedOptionId,
            );
            const resolved = resolveShippingCost(selectedOption);
            selectedShippingCost = resolved.cost;
        }

        const data: OrderCalculationRequest = {
            [OrderCalculationRequestFields.Products]: getProductsFromCartItems(),
            [OrderCalculationRequestFields.CouponName]: null,
            [OrderCalculationRequestFields.ShippingOptionId]: selectedOptionId || null,
            [OrderCalculationRequestFields.ShippingCost]: selectedShippingCost,
        };

        const response = await HttpOrder.calculate(data);

        if (!response[BaseResponseFields.HasError]) {
            setCouponStatus({
                loading: false,
                error: null,
                success: false,
                appliedDiscount: null,
                calculationData: response[BaseResponseFields.Data],
            });
        }
    };

    // deprecado, no se obtienen mas las opciones de envio al cargar la pagina, sino que se calculan
    // cuando se introduce el cp
    //useEffect(() => {
    //  HttpShippings.getShippingOptions().then(setShippingOptions).catch(console.error);
    //}, []);

    useEffect(() => {
        const zipCodeError = methods.getFieldState(nameZipCode);
        if (zipCodeError && zipCodeError.invalid) {
            methods.clearErrors(nameZipCode);
        }
    }, [watchZipCode]);

    useEffect(() => {
        const mailError = methods.getFieldState(nameMail);
        if (mailError && mailError.invalid) {
            methods.clearErrors(nameMail);
        }
    }, [watchMail]);

    useEffect(() => {
        if (!orderGuid) return;
        if (lastSyncedMailRef.current !== null || lastSyncedZipRef.current !== null) return;

        const currentMail = methods.getValues(nameMail);
        const currentZip = methods.getValues(nameZipCode);

        const deliveryValueRaw = methods.getValues(CheckoutFormTypeFields.DeliveryType) as any;
        const deliveryValue = deliveryValueRaw ? String(deliveryValueRaw) : null;

        const { optionId, branchId } = parseDeliveryValue(deliveryValue);

        if (optionId && shippingOptions.length === 0) return;

        let currentSelectedShippingCost: number | null = null;
        if (optionId) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === optionId,
            );
            currentSelectedShippingCost = resolveShippingCost(selectedOption).cost;
        }

        const currentPaymentOptionIdRaw = methods.getValues(
            CheckoutFormTypeFields.PaymentOptionId,
        ) as any;

        lastSyncedMailRef.current = currentMail;
        lastSyncedZipRef.current = currentZip;
        lastSyncedDeliveryTypeRef.current = deliveryValue; // raw: optionId::branchId
        lastSyncedDeliveryBranchRef.current = branchId ?? null;
        lastSyncedShippingCostRef.current = currentSelectedShippingCost;
        lastSyncedPaymentOptionRef.current = currentPaymentOptionIdRaw
            ? String(currentPaymentOptionIdRaw)
            : null;
    }, [orderGuid, shippingOptions]);

    useEffect(() => {
        if (!confirmedZipCode) return;
        if (isLoadingZipCode || isLoadingCustomerDetail || isLoadingOrder) return;

        const currentMail = methods.getValues(nameMail);
        const currentZip = methods.getValues(nameZipCode);

        const deliveryValueRaw = methods.getValues(CheckoutFormTypeFields.DeliveryType) as any;
        const deliveryValue = deliveryValueRaw ? String(deliveryValueRaw) : null;

        const { optionId: currentOptionId, branchId: currentBranchId } =
            parseDeliveryValue(deliveryValue);

        if (currentOptionId && shippingOptions.length === 0) return;

        let currentSelectedShippingCost: number | null = null;
        if (currentOptionId) {
            const selectedOption = shippingOptions.find(
                (opt) => opt[EntityWithIdFields.Id] === currentOptionId,
            );
            if (selectedOption) {
                currentSelectedShippingCost = resolveShippingCost(selectedOption).cost;
            }
        }

        const mailOk = RequiredMailSchema.isValidSync(currentMail || "");
        const zipOk = RequiredStringSchema.isValidSync(currentZip || "");
        const deliveryOk =
            !!currentOptionId &&
            currentSelectedShippingCost !== null &&
            currentSelectedShippingCost !== undefined;

        if (!mailOk || !zipOk || !deliveryOk) return;

        const currentPaymentOptionIdRaw = methods.getValues(
            CheckoutFormTypeFields.PaymentOptionId,
        ) as any;
        const currentPaymentOptionId = currentPaymentOptionIdRaw
            ? String(currentPaymentOptionIdRaw)
            : null;

        if (
            !!orderGuidRef.current &&
            lastSyncedMailRef.current === currentMail &&
            lastSyncedZipRef.current === currentZip &&
            lastSyncedDeliveryTypeRef.current === deliveryValue &&
            lastSyncedShippingCostRef.current === currentSelectedShippingCost &&
            lastSyncedPaymentOptionRef.current === currentPaymentOptionId
        ) {
            return;
        }

        const timeout = window.setTimeout(() => {
            void createOrUpdateOrder(undefined, false);
        }, 600);

        return () => window.clearTimeout(timeout);
    }, [
        watchMail,
        watchZipCode,
        watchDeliveryType,
        confirmedZipCode,
        shippingOptions,
        isLoadingZipCode,
        isLoadingCustomerDetail,
        isLoadingOrder,
        watchPaymentOptionId,
    ]);

    useEffect(() => {
        if (!!watchZipCode)
            methods.setValue(CheckoutFormTypeFields.DeliveryAddress, {
                [OrderDeliveryAddressFields.ZipCode]: watchZipCode,
                [OrderDeliveryAddressFields.Street]: null,
                [OrderDeliveryAddressFields.StreetNumber]: null,
                [OrderDeliveryAddressFields.FloorApartment]: null,
                [OrderDeliveryAddressFields.ProvinceCode]: 0,
                [OrderDeliveryAddressFields.LocalityDesc]: null,
            } as OrderDeliveryAddress);
    }, [watchZipCode]);

    useEffect(() => {
        if (!selectedOptionId) return;

        const selectedOption = shippingOptions.find(
            (opt) => opt[EntityWithIdFields.Id] === selectedOptionId,
        );
        const requiresAddress =
            selectedOption?.[OrderShippingViewDTOFields.RequestAddress] ?? false;

        if (!requiresAddress) {
            methods.setValue(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.Street}` as any,
                null,
            );
            methods.setValue(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.StreetNumber}` as any,
                null,
            );
            methods.setValue(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.FloorApartment}` as any,
                null,
            );
            methods.setValue(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ProvinceCode}` as any,
                0,
            );
            methods.setValue(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.LocalityDesc}` as any,
                null,
            );

            methods.clearErrors(CheckoutFormTypeFields.DeliveryAddress);
        }
    }, [watchDeliveryType, shippingOptions]);

    useEffect(() => {
        if (!selectedOptionId || cartItems.length === 0) return;

        const selectedOption = shippingOptions.find(
            (opt) => opt[EntityWithIdFields.Id] === selectedOptionId,
        );
        const resolved = resolveShippingCost(selectedOption);

        const data: OrderCalculationRequest = {
            [OrderCalculationRequestFields.Products]: getProductsFromCartItems(),
            [OrderCalculationRequestFields.CouponName]:
                watchCouponStatus?.appliedDiscount?.[AppliedCouponDiscountFields.Name] || null,
            [OrderCalculationRequestFields.ShippingOptionId]: selectedOptionId || null,
            [OrderCalculationRequestFields.ShippingCost]: resolved.cost,
        };

        HttpOrder.calculate(data)
            .then((response) => {
                if (!response[BaseResponseFields.HasError]) {
                    const calculationData = response[BaseResponseFields.Data];

                    if (watchCouponStatus?.appliedDiscount) {
                        const appliedDiscount: AppliedCouponDiscount = {
                            [AppliedCouponDiscountFields.Id]:
                                calculationData[EntityWithIdFields.Id] ||
                                watchCouponStatus.appliedDiscount[AppliedCouponDiscountFields.Id],
                            [AppliedCouponDiscountFields.Name]:
                                watchCouponStatus.appliedDiscount[AppliedCouponDiscountFields.Name],
                            [AppliedCouponDiscountFields.DiscountTotal]:
                                calculationData[OrderCalculationViewFields.Total],
                            [AppliedCouponDiscountFields.OriginalTotal]:
                                calculationData[OrderCalculationViewFields.Subtotal],
                            [AppliedCouponDiscountFields.FreeShipping]:
                                calculationData[OrderCalculationViewFields.ShippingDiscount] > 0,
                        };

                        setCouponStatus({
                            ...watchCouponStatus,
                            appliedDiscount,
                            calculationData,
                        });
                    } else {
                        setCouponStatus({
                            loading: false,
                            error: null,
                            success: false,
                            appliedDiscount: null,
                            calculationData,
                        });
                    }
                }
            })
            .catch((error) => {
                console.error("Error calculating order with shipping:", error);
            });
    }, [selectedOptionId, shippingOptions, cartItems]);

    useEffect(() => {
        if (watchDeliveryType === undefined && cartItems.length > 0) {
            const data: OrderCalculationRequest = {
                [OrderCalculationRequestFields.Products]: getProductsFromCartItems(),
                [OrderCalculationRequestFields.CouponName]:
                    watchCouponStatus?.appliedDiscount?.[AppliedCouponDiscountFields.Name] || null,
                [OrderCalculationRequestFields.ShippingOptionId]: null,
                [OrderCalculationRequestFields.ShippingCost]: null,
            };

            HttpOrder.calculate(data)
                .then((response) => {
                    if (!response[BaseResponseFields.HasError]) {
                        const calculationData = response[BaseResponseFields.Data];

                        if (watchCouponStatus?.appliedDiscount) {
                            const appliedDiscount: AppliedCouponDiscount = {
                                [AppliedCouponDiscountFields.Id]:
                                    calculationData[EntityWithIdFields.Id] ||
                                    watchCouponStatus.appliedDiscount[
                                        AppliedCouponDiscountFields.Id
                                    ],
                                [AppliedCouponDiscountFields.Name]:
                                    watchCouponStatus.appliedDiscount[
                                        AppliedCouponDiscountFields.Name
                                    ],
                                [AppliedCouponDiscountFields.DiscountTotal]:
                                    calculationData[OrderCalculationViewFields.Total],
                                [AppliedCouponDiscountFields.OriginalTotal]:
                                    calculationData[OrderCalculationViewFields.Subtotal],
                                [AppliedCouponDiscountFields.FreeShipping]:
                                    calculationData[OrderCalculationViewFields.ShippingDiscount] >
                                    0,
                            };

                            setCouponStatus({
                                ...watchCouponStatus,
                                appliedDiscount,
                                calculationData,
                            });
                        } else {
                            setCouponStatus({
                                loading: false,
                                error: null,
                                success: false,
                                appliedDiscount: null,
                                calculationData,
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error calculating order without shipping:", error);
                });
        }
    }, [watchDeliveryType, cartItems]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (didLoadProfileDataRef.current) return;
        didLoadProfileDataRef.current = true;

        (async () => {
            try {
                const u = await HttpAuth.getUser();
                setUser(u);

                if (!clients || clients.length === 0) {
                    try {
                        const c = await HttpUserClient.getAllClients();
                        setClients(c ?? []);
                    } catch (err) {
                        // silencioso - usuario no logueado
                        console.debug("Error loading clients:", err);
                    }
                }

                if (!addresses || addresses.length === 0) {
                    try {
                        const a = await HttpUserAddress.getAllAdresses();
                        setAddresses(a ?? []);
                    } catch (err) {
                        // silencioso - usuario no logueado
                        console.debug("Error loading addresses:", err);
                    }
                }
            } catch (err) {
                // usuario no logueado - esto es esperado
                console.debug("User not authenticated:", err);
                setUser(null);
            }
        })().catch((err) => {
            // doble catch para asegurar que ningún error lance a ErrorBoundary
            console.debug("Unexpected error in profile autofill:", err);
            setUser(null);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldSkipProfileAutofill]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (!user) return;

        // mail
        if ((user as any)?.mail) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}` as const,
                (user as any).mail,
            );
        }

        const countryOrArea = onlyDigits(
            (user as any)?.codigoArea ??
                (user as any)?.areaCode ??
                (user as any)?.countryCode ??
                "",
        );

        let phone = onlyDigits(
            (user as any)?.telefono ?? (user as any)?.phoneNumber ?? (user as any)?.phone ?? "",
        );

        if (!countryOrArea && phone.startsWith("54") && phone.length > 10) {
            // AR
            phone = phone.slice(2);
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.AreaCode}` as const,
                "54",
            );
        } else if (!countryOrArea && phone.startsWith("598") && phone.length > 9) {
            // UY
            phone = phone.slice(3);
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.AreaCode}` as const,
                "598",
            );
        } else if (countryOrArea) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.AreaCode}` as const,
                countryOrArea,
            );
        }

        if (phone) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.PhoneNumber}` as const,
                phone,
            );
        }
    }, [user, shouldSkipProfileAutofill]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (!activeClient) return;

        const PERSONA_JURIDICA = 3;

        const codPersonaTipo =
            (activeClient as any)?.codPersonaTipo ??
            (activeClient as any)?.[UserClientFields.PersonTypeId];

        const isJuridica = `${codPersonaTipo}` === `${PERSONA_JURIDICA}`;

        const razonSocial = String(
            (activeClient as any)?.razonSocial ??
                (activeClient as any)?.[UserClientFields.CompanyName] ??
                "",
        ).trim();

        const nombre = isJuridica
            ? razonSocial
            : String(
                  (activeClient as any)?.nombre ??
                      (activeClient as any)?.[UserClientFields.Name] ??
                      "",
              ).trim();

        const apellido = isJuridica
            ? razonSocial
            : String(
                  (activeClient as any)?.apellido ??
                      (activeClient as any)?.[UserClientFields.Surname] ??
                      "",
              ).trim();

        const docRaw =
            (activeClient as any)?.numeroDocumento ??
            (activeClient as any)?.[UserClientFields.IdentityDocumentNumber];

        const doc = onlyDigits(docRaw);

        if (nombre) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.FirstName}` as const,
                nombre,
            );
        }

        if (apellido) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.LastName}` as const,
                apellido,
            );
        }

        if (doc) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Document}` as const,
                doc,
            );
        }
    }, [activeClient, shouldSkipProfileAutofill]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (!activeAddress) return;

        const zip =
            (activeAddress as any)?.codigoPostal ??
            (activeAddress as any)?.[UserAddressFields.ZipCode];

        if (zip) {
            setIfUserHasNotEdited(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}` as const,
                zip,
            );
            shouldAutoSubmitZipRef.current = true;
        }
    }, [activeAddress, shouldSkipProfileAutofill]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (!shouldAutoSubmitZipRef.current) return;
        if (didAutoSubmitZipRef.current) return;

        if (confirmedZipCode) {
            didAutoSubmitZipRef.current = true;
            return;
        }

        const zip = methods.getValues(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}` as const,
        );

        const mail = methods.getValues(
            `${CheckoutFormTypeFields.PersonalInformation}.${OrderPersonalInformationFields.Email}` as const,
        );

        if (!zip || !mail) return;

        didAutoSubmitZipRef.current = true;
        void onSubmitZip();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchZipCode, watchMail, confirmedZipCode, shouldSkipProfileAutofill]);

    useEffect(() => {
        if (shouldSkipProfileAutofill) return;
        if (!activeAddress) return;
        if (!selectedOptionId) return;

        const selectedOption = shippingOptions.find(
            (opt) => `${opt?.[EntityWithIdFields.Id]}` === `${selectedOptionId}`,
        );

        const requiresAddress =
            selectedOption?.[OrderShippingViewDTOFields.RequestAddress] ?? false;
        if (!requiresAddress) return;

        const formZip = onlyDigits(
            methods.getValues(
                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}` as const,
            ),
        );

        const selectedZip = onlyDigits(
            (activeAddress as any)?.codigoPostal ??
                (activeAddress as any)?.[UserAddressFields.ZipCode] ??
                "",
        );

        if (formZip && selectedZip && `${formZip}` !== `${selectedZip}`) return;

        const street =
            (activeAddress as any)?.calle ?? (activeAddress as any)?.[UserAddressFields.Street];

        const streetNumber =
            (activeAddress as any)?.numero ??
            (activeAddress as any)?.[UserAddressFields.StreetNumber];

        const floorApt =
            (activeAddress as any)?.pisoDpto ??
            (activeAddress as any)?.[UserAddressFields.FloorApartment] ??
            null;

        const provinceCode =
            (activeAddress as any)?.codProvincia ??
            (activeAddress as any)?.[UserAddressFields.ProvinceId] ??
            0;

        const localityDesc =
            (activeAddress as any)?.descLocalidad ??
            (activeAddress as any)?.[UserAddressFields.CityDesc];

        setIfUserHasNotEdited(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.Street}` as const,
            street,
        );
        setIfUserHasNotEdited(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.StreetNumber}` as const,
            streetNumber,
        );
        setIfUserHasNotEdited(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.FloorApartment}` as const,
            floorApt,
        );
        setIfUserHasNotEdited(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ProvinceCode}` as const,
            provinceCode,
        );
        setIfUserHasNotEdited(
            `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.LocalityDesc}` as const,
            localityDesc,
        );
    }, [
        shouldSkipProfileAutofill,
        activeAddress,
        selectedOptionId,
        shippingOptions,
        watchDeliveryType,
    ]);

    const focusFirstError = (errors: FieldErrors<CheckoutFormType>) => {
        const findFirstPath = (node: any, path: string[] = []): string[] | null => {
            if (!node || typeof node !== "object") return null;
            if (typeof node.message === "string" && node.type) return path;

            for (const key of Object.keys(node)) {
                const found = findFirstPath(node[key], [...path, key]);
                if (found) return found;
            }
            return null;
        };

        const path = findFirstPath(errors);
        if (!path) return;

        const name = path.join(".");
        const el = document.getElementsByName(name)[0] as HTMLElement | undefined;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        el?.focus?.();
    };

    const onInvalidSubmit = (errors: FieldErrors<CheckoutFormType>) => {
        enqueueSnackbar("Hay campos obligatorios sin completar", { variant: "error" });
        focusFirstError(errors);
    };

    return (
        <Outlet
            context={{
                methods,
                currentStep,
                setCurrentStep,
                processStatus,
                confirmedZipCode,
                onCancelZipCode,
                changeDeliveryZip,
                onSubmitZip,
                // @ts-ignore
                onSubmitCustomerDetail: methods.handleSubmit(
                    onSubmitCustomerDetail,
                    onInvalidSubmit,
                ),
                // @ts-ignore
                onSubmitOrder: methods.handleSubmit(onHandleSubmit, onInvalidSubmit),
                deliveryType: watchDeliveryType,
                setDeliveryType,
                isHomeDelivery: isHomeDelivery,
                deliveryOptions: deliveryOptions,
                zipCode: watchZipCode,
                showCouponField,
                setShowCouponField,
                couponCode,
                setCouponCode,
                cartItems,
                couponStatus: watchCouponStatus,
                setCouponStatus,
                applyCoupon,
                isLoadingZipCode,
                isLoadingCustomerDetail,
                isLoadingOrder,
                recalculateOrderWithoutCoupon,
                recalculateOrder,
                shippingOptions,
                minPurchaseAmount: MIN_PURCHASE_AMOUNT,
                minPurchaseSubtotal,
                minPurchaseMissing,
                isCartEmpty,
                isCheckoutBlocked,
                paymentOptions,
                isLoadingPaymentOptions,
                paymentOptionId: watchPaymentOptionId,
                setPaymentOptionId,
                setPaymentData,
                getPaymentData,
                selectedPaymentProviderCode,
                cardTypes,
                isLoadingCardTypes,
                lstCamposAdicionales,
                camposAdicionalesValores,
                setCampoAdicionalValor,
            }}
        />
    );
}

export const useCheckoutForm = () => {
    return useOutletContext<CheckoutFormContextType>();
};
